import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParamsControl } from '../../utils/useParamsControl'
import { AppTitlePage } from '../../components/AppTitlePage'
import { AppBtnColor } from '../../components/btn/AppBtn'
import { AllRoutes } from '../../core/routes/AllRoutes'
import { ShowFormatType } from './BuffetReportsVZ'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { createQueryString } from '../../utils/createQueryString'
import { GetFile } from '../../utils/getFile'
import { BASE_URL } from '../../core/api/BaseApi'
import {
  AppNotification,
  AppNotificationUpdate,
  NotificationType,
} from '../../components/notification/Notification'
import { AppLoader, LoaderType } from 'components/AppLoader'
import { useTranslation } from 'react-i18next'
import { IBuffetGeneralReportChartRequest } from 'core/api/dto/BuffetGeneralReportDto'
import moment from 'moment'
import './BuffetReportVZChart.scss'
import { Line } from 'react-chartjs-2'
import {
  useGetBuffetGeneralReportChartQuery,
  useGetBuffetGeneralReportQuery,
} from 'core/api/BuffetReportApi'
import { ChartPaginationBlock } from './components/ChartPaginationBlock/ChartPaginationBlock'
import CheckboxForChart from './components/CheckboxForChart/CheckboxForChart'
import { colorsArr } from 'helpers/colors'
import { showFormatHeader } from './BuffetReportsVZLine'
import { AppScrollWrapperNew } from 'widgets/AppScrollWrapper/AppScrollWrapper'

interface IChartData {
  label: string
  data: IChartDataData[]
  id: number
  borderColor?: string
}
interface IChartDataData {
  id: number
  label: string
  time: string
  actualWeight: number
  dimensionsCount: number
  writeOff: number
  serving: number
  refund: number
}
type LegendesActive = {
  id: number
  active: boolean
}

export const BuffetReportsVZChart = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isLoadingLocal, setIsLoadingLocal] = useState<boolean>(false)
  const [legendesActive, setLegendesActive] = useState<LegendesActive[]>([])
  const [dataForFilterChart, setDataForFilterChart] = useState<IChartData[]>([])
  const pageParams: any = useParamsControl<
    IBuffetGeneralReportChartRequest,
    keyof IBuffetGeneralReportChartRequest
  >({
    withPagination: true,
    whiteList: [
      'report_type',
      'dimension_date_gte',
      'dimension_date_lte',
      'dish_ids',
      'dimension_types',
      'plan_menu_type_ids',
      'dimension_date',
      'interval_minutes',
      'dimension_time_gte',
      'dimension_time_lte',
      'internal_dish_category_ids',
    ],
  })

  const { data: generalReport } = useGetBuffetGeneralReportQuery(
    { ...pageParams, offset: undefined },
    { skip: !pageParams },
  )

  const { data: generalReportChartData, isLoading } =
    useGetBuffetGeneralReportChartQuery(
      { ...pageParams, offset: undefined },
      { skip: !pageParams, refetchOnMountOrArgChange: true },
    )
  const [chartData, setChartData] = useState<IChartData[]>([])

  useEffect(() => {
    if (generalReportChartData) {
      setLegendesActive(
        generalReportChartData.results.map((e) => ({ id: e.id, active: true })),
      )
      setChartData(
        generalReportChartData.results.map((e, i) => ({
          label: e.name,
          id: e.id,
          data: e.date.charts.map((elem) => ({
            id: e.id,
            label: e.name,
            ...elem,
          })),
          borderColor: colorsArr[i],
        })),
      )
      setDataForFilterChart(
        generalReportChartData.results.map((e, i) => ({
          label: e.name,
          id: e.id,
          data: e.date.charts.map((elem) => ({
            id: e.id,
            label: e.name,
            ...elem,
          })),
          borderColor: colorsArr[i],
        })),
      )
    }
  }, [generalReportChartData, pageParams])

  useEffect(() => {
    const chartDataFiltered = dataForFilterChart.filter(
      (el) => legendesActive.find((e) => e.id === el.id)?.active,
    )
    setChartData(chartDataFiltered)
  }, [dataForFilterChart, legendesActive])

  const unloadDishesHandler = () => {
    setIsLoadingLocal(true)
    const toastId = 'report-export-chart'
    AppNotification({
      msg: t('Формируется файл, ожидайте…'),
      type: NotificationType.info,
      autoClose: false,
      toastId,
    })
    const params = createQueryString(pageParams)
    GetFile(`${BASE_URL}/report_buffet/export_general_report_to_file/${params}`)
      .then((success) =>
        AppNotificationUpdate(toastId, {
          msg: success
            ? t('Отчет успешно выгружен!')
            : t('Не удалось сформировать отчет'),
          type: success ? NotificationType.success : NotificationType.error,
        }),
      )
      .finally(() => setIsLoadingLocal(false))
  }

  const actions = useMemo(
    () => [
      {
        btnTitle: isLoadingLocal ? 'Идет выгрузка' : 'Выгрузить',
        btnIco: 'export',
        items: [
          {
            title: 'Таблица .XLSX',
            onClick: unloadDishesHandler,
            ico: 'file-xlsx',
          },
          // { title: 'Журнал .PDF', onClick: () => {}, ico: 'file-pdf' },
          // { title: 'Экспортировать .CSV', onClick: () => {}, ico: 'file-csv' },
        ],
        color: AppBtnColor.whiteOutlineViolet,
        disabled: !generalReport || isLoadingLocal,
      },
    ],
    [pageParams, generalReport, isLoadingLocal],
  )

  const [isChart, setIsChart] = useState(true)
  const onChangeChart = (value: boolean) => setIsChart(value)

  const goToTable = useCallback(
    () =>
      navigate(
        `/${AllRoutes.buffetReportsVZ.path}/${
          AllRoutes.buffetReportsVZLine.path
        }/?${searchParams.toString()}`,
      ),
    [navigate, searchParams],
  )

  useEffect(() => {
    if (!isChart) goToTable()
  }, [isChart, goToTable])

  const onActiveLegend = (id: number, value: boolean) => {
    setLegendesActive((prev) =>
      prev.map((el) => (el.id === id ? { ...el, active: value } : el)),
    )
  }

  const getChartDataV2 = () => {
    const labels =
      chartData.length > 0 ? chartData[0].data.map((d) => d.time) : []
    return {
      labels,
      datasets: chartData.map((cd) => ({
        label: cd.label,
        data: cd.data.map((d) => d.actualWeight),
        borderColor: cd.borderColor,
        fill: false,
      })),
    }
  }

  return (
    <div className="width-for-page-wrapper buffet-comparative-report-detail general-report">
      {!!isLoading && <AppLoader loaderType={LoaderType.main} />}
      <AppTitlePage
        title={`${moment(pageParams?.dimension_date_gte).format(
          'DD.MM.YYYY',
        )} - ${moment(pageParams?.dimension_date_lte).format(
          'DD.MM.YYYY',
        )};</br> ${t(
          showFormatHeader[pageParams?.report_type as ShowFormatType],
        )}`}
        actions={chartData ? actions : undefined}
        goBack
        goBackPath={`/${AllRoutes.buffetReportsVZ.path}/?${searchParams
          .toString()
          .replace(
            `&dimension_date=${searchParams.get('dimension_date')}`,
            '',
          )}`}
        isReportIntensity={pageParams?.report_type === ShowFormatType.intensity}
        isReportIntensityChart={isChart}
        onChangeChart={onChangeChart}
      />
      {!!(
        chartData &&
        pageParams?.report_type === ShowFormatType.intensity &&
        isChart
      ) && (
        <div style={{ marginTop: 15 }}>
          <div className="app-chart-wrap">
            {generalReportChartData && (
              <div className="legend">
                <AppScrollWrapperNew>
                  {generalReportChartData.results.length ? (
                    generalReportChartData.results.map((e, i) => (
                      <div className="legend-item" key={e.id}>
                        <div
                          className="legend-color"
                          style={{ background: `${colorsArr[i]}` }}
                        />
                        <CheckboxForChart
                          key={e.id}
                          name={`${e.name}-${e.id}`}
                          value={
                            legendesActive.find((el) => el.id === e.id)
                              ?.active || false
                          }
                          text={e.name}
                          onChange={(value) => onActiveLegend(e.id, value)}
                        />
                      </div>
                    ))
                  ) : (
                    <p className="color-Red text-center">{t('Нет данных')}</p>
                  )}
                </AppScrollWrapperNew>
              </div>
            )}
            <div className="chart">
              <div className="chart-wrap">
                <Line
                  data={getChartDataV2()}
                  options={{
                    responsive: true,
                    legend: {
                      display: false,
                    },
                    tooltips: {
                      enabled: true,
                      mode: 'nearest',
                      intersect: true,
                    },
                    scales: {
                      xAxes: [
                        {
                          scaleLabel: {
                            display: true,
                            labelString: t('Время'),
                            fontSize: 22,
                            fontStyle: 'bold',
                          },
                        },
                      ],
                      yAxes: [
                        {
                          scaleLabel: {
                            display: true,
                            labelString: t('Количество, кг'),
                            fontSize: 22,
                            fontStyle: 'bold',
                          },
                        },
                      ],
                    },
                  }}
                />
              </div>
              {generalReportChartData && (
                <ChartPaginationBlock
                  dataCount={generalReportChartData.count}
                  dataNext={generalReportChartData.next}
                  date={generalReportChartData.currentDate}
                  dataPrevious={generalReportChartData.previous}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
